import React, { useCallback, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { CalendarIcon, RefreshCw } from 'lucide-react';
import { format, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { PeriodOption } from '@/types/dashboard';
import { getCurrentDateBR, getCurrentDateBRAsDate } from '@/utils/dateUtils';

interface DateRangeSelectorProps {
  selectedPeriod: PeriodOption;
  onPeriodChange: (period: PeriodOption) => void;
  onRefresh: () => void;
  isLoading?: boolean;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  selectedPeriod,
  onPeriodChange,
  onRefresh,
  isLoading = false
}) => {
  // Estados para período personalizado
  const [customDateFrom, setCustomDateFrom] = useState<Date | undefined>(undefined);
  const [customDateTo, setCustomDateTo] = useState<Date | undefined>(undefined);
  const [isFromCalendarOpen, setIsFromCalendarOpen] = useState(false);
  const [isToCalendarOpen, setIsToCalendarOpen] = useState(false);

  // Função auxiliar para converter string dd/MM/yyyy para Date
  const parseDateBR = useCallback((dateString: string): Date | undefined => {
    try {
      return parse(dateString, 'dd/MM/yyyy', new Date());
    } catch {
      return undefined;
    }
  }, []);

  // Inicializar datas personalizadas quando o período customizado é selecionado
  useEffect(() => {
    if (selectedPeriod.value === 'custom') {
      const fromDate = parseDateBR(selectedPeriod.startDate);
      const toDate = parseDateBR(selectedPeriod.endDate);
      // Se não houver datas válidas, inicializar com a data atual
      if (!fromDate || !toDate) {
        const today = getCurrentDateBRAsDate();
        setCustomDateFrom(today);
        setCustomDateTo(today);
      } else {
        setCustomDateFrom(fromDate);
        setCustomDateTo(toDate);
      }
    } else {
      // Limpar calendários quando não for período personalizado
      setIsFromCalendarOpen(false);
      setIsToCalendarOpen(false);
    }
  }, [selectedPeriod.value, selectedPeriod.startDate, selectedPeriod.endDate, parseDateBR]);

  // Atualizar período quando datas personalizadas mudarem
  useEffect(() => {
    if (selectedPeriod.value === 'custom' && customDateFrom && customDateTo) {
      const newStartDate = format(customDateFrom, 'dd/MM/yyyy');
      const newEndDate = format(customDateTo, 'dd/MM/yyyy');
      
      // Só atualizar se as datas realmente mudaram
      if (selectedPeriod.startDate !== newStartDate || selectedPeriod.endDate !== newEndDate) {
        const newPeriod: PeriodOption = {
          label: 'Personalizado',
          value: 'custom',
          startDate: newStartDate,
          endDate: newEndDate
        };
        onPeriodChange(newPeriod);
      }
    }
  }, [customDateFrom, customDateTo, selectedPeriod.value, selectedPeriod.startDate, selectedPeriod.endDate, onPeriodChange]);

  // CORREÇÃO: Calcular opções de período dinamicamente
  const getPeriodOptions = useCallback((): PeriodOption[] => {
    const dataAtual = getCurrentDateBR();
    const currentDateBR = getCurrentDateBRAsDate();
    
    console.log('🕐 [DateRangeSelector] Calculando opções de período com data atual:', dataAtual);
    
    return [
      {
        label: 'Hoje',
        value: 'today',
        startDate: dataAtual,
        endDate: dataAtual
      },
      {
        label: 'Últimos 7 dias',
        value: 'last7days',
        startDate: format(new Date(currentDateBR.getTime() - 6 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy'),
        endDate: dataAtual
      },
      {
        label: 'Últimos 30 dias',
        value: 'last30days',
        startDate: format(new Date(currentDateBR.getTime() - 29 * 24 * 60 * 60 * 1000), 'dd/MM/yyyy'),
        endDate: dataAtual
      },
      {
        label: 'Este mês',
        value: 'thisMonth',
        startDate: format(new Date(currentDateBR.getFullYear(), currentDateBR.getMonth(), 1), 'dd/MM/yyyy'),
        endDate: dataAtual
      },
      {
        label: 'Mês passado',
        value: 'lastMonth',
        startDate: format(new Date(currentDateBR.getFullYear(), currentDateBR.getMonth() - 1, 1), 'dd/MM/yyyy'),
        endDate: format(new Date(currentDateBR.getFullYear(), currentDateBR.getMonth(), 0), 'dd/MM/yyyy')
      },
      {
        label: 'Personalizado',
        value: 'custom',
        startDate: customDateFrom ? format(customDateFrom, 'dd/MM/yyyy') : getCurrentDateBR(),
        endDate: customDateTo ? format(customDateTo, 'dd/MM/yyyy') : getCurrentDateBR()
      }
    ];
  }, [customDateFrom, customDateTo]);

  const periodOptions = getPeriodOptions();
  const isCustomPeriod = selectedPeriod.value === 'custom';

  return (
    <div className="flex flex-col gap-4 p-4 bg-white rounded-lg shadow-sm border">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Período:</span>
          <Select
            value={selectedPeriod.value}
            onValueChange={(value) => {
              const period = periodOptions.find(p => p.value === value);
              if (period) {
                onPeriodChange(period);
              }
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Selecione o período" />
            </SelectTrigger>
            <SelectContent>
              {periodOptions.map((period) => (
                <SelectItem key={period.value} value={period.value}>
                  {period.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!isCustomPeriod && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>De:</span>
            <span className="font-medium">
              {selectedPeriod.startDate}
            </span>
            <span>até:</span>
            <span className="font-medium">
              {selectedPeriod.endDate}
            </span>
          </div>
        )}

        {isCustomPeriod && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-700">Data início:</Label>
              <Popover open={isFromCalendarOpen} onOpenChange={setIsFromCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-11 justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customDateFrom ? format(customDateFrom, 'dd/MM/yyyy') : 'Data início'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border shadow-lg z-50" align="start" side="bottom">
                  <Calendar
                    mode="single"
                    selected={customDateFrom}
                    onSelect={(date) => {
                      setCustomDateFrom(date);
                      setIsFromCalendarOpen(false);
                    }}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-700">Até:</Label>
              <Popover open={isToCalendarOpen} onOpenChange={setIsToCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-11 justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {customDateTo ? format(customDateTo, 'dd/MM/yyyy') : 'Data fim'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border shadow-lg z-50" align="start" side="bottom">
                  <Calendar
                    mode="single"
                    selected={customDateTo}
                    onSelect={(date) => {
                      setCustomDateTo(date);
                      setIsToCalendarOpen(false);
                    }}
                    locale={ptBR}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}

        <Button
          onClick={onRefresh}
          disabled={isLoading}
          variant="outline"
          size="sm"
          className="ml-auto"
        >
          <RefreshCw className={cn("w-4 h-4 mr-2", isLoading && "animate-spin")} />
          Atualizar
        </Button>
      </div>
    </div>
  );
};

export default DateRangeSelector;
